import { useEffect, useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { useAuth } from "@/hooks/useAuth";
import { useFeedbackPromptDwell } from "@/hooks/useFeedbackPromptDwell";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels } from "@/i18n";
import { UserType } from "@/types/user";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

/**
 * Soft “what do you think?” prompt after ~30 min active time (30s in DEV).
 * CTA opens the normal FeedbackDialog; dismiss persists in localStorage.
 */
export function FeedbackPromptTip() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const labels = dashboardLabels(language)?.feedbackPrompt;

  const enabled =
    Boolean(user?.id) && user?.type !== UserType.Admin && Boolean(labels);

  const { shouldShow, dismiss } = useFeedbackPromptDwell(user?.id, enabled);
  const [tipOpen, setTipOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    setTipOpen(shouldShow);
  }, [shouldShow]);

  if (!enabled || !labels) return null;

  const handleTipOpenChange = (open: boolean) => {
    if (!open) {
      dismiss();
      setTipOpen(false);
      return;
    }
    setTipOpen(true);
  };

  const openFeedback = () => {
    dismiss();
    setTipOpen(false);
    // Let tip close before opening feedback (avoids nested dialog glitches).
    window.setTimeout(() => setFeedbackOpen(true), 280);
  };

  return (
    <>
      <Dialog open={tipOpen} onOpenChange={handleTipOpenChange}>
        <DialogContent
          className={cn(
            "sm:max-w-md",
            isNative && "native-sheet-form",
          )}
        >
          <DialogHeader>
            <div className="mb-1 flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent/15 text-accent">
                <MessageSquarePlus className="h-5 w-5" aria-hidden />
              </span>
              <DialogTitle>{labels.title}</DialogTitle>
            </div>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              {labels.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button type="button" className="w-full" onClick={openFeedback}>
              {labels.cta}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => handleTipOpenChange(false)}
            >
              {labels.later}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}
